import { Injectable } from '@angular/core';
import {
  CBasketConfigurationKeys,
  CBASKET_NAMESPACE,
} from '@epicuro-next/data/baskets';
import { ExtendedDtoBasket } from '@epicuro-next/data/baskets-dto';
import {
  ConfigurationsService,
  initConfigSuccess,
  updateRules,
  upsertToConfig,
} from '@epicuro-next/platform/configuration';
import { IRuleContext } from '@epicuro-next/platform/rule-engine';
import { MessageType, showSnackBarMessage } from '@epicuro-next/state/snackbar';
import {
  distinctUntilChangedBy,
  isNumber,
  isString,
  uniqueBy,
} from '@epicuro-next/utilities';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { BasketStatus, OrderType } from 'graphql-api/baskets-api-interfaces';
import {
  catchError,
  combineLatest,
  EMPTY,
  filter,
  forkJoin,
  map,
  merge,
  mergeMap,
  Observable,
  switchMap,
  take,
  tap,
} from 'rxjs';

import { CBasketService } from '../../services';
import { isBasketStatusNewish } from '../../shared';
import {
  ManualDiscountsValidationCDiscountOrderTypes,
  MaxDiscountPercentageValue,
} from '../../types';
import {
  applyBasketManualDiscounts,
  applyBasketManualDiscountsInvalidated,
  applyBasketManualDiscountsValidated,
  clearBasketManualDiscounts,
  loadBasketManualDiscounts,
  loadDtoBasketSuccess,
  loadMaxDiscountPercentage,
  loadMaxDiscountPercentageForMaterials,
  loadMaxDiscountPercentageForMaterialsSuccess,
  loadMaxDiscountPercentageSuccess,
  manualDiscountsDeleteSuccess,
  manualDiscountsUpdatedFailed,
  manualDiscountsUpdatedSuccess,
  manualDiscountValueBelowCDiscount,
  maxBasketManualDiscountValueExceeded,
  removeBasketManualDiscounts,
  resetBasketManualDiscountsInputs,
  reverseCalculationForDiscountPercentageFromNetTotalValueFailed,
} from '../actions';
import { ManualDiscountEntity } from '../reducers/basket-manual-discounts.reducer';
import {
  selectAllBasketManualDiscounts,
  selectBasketItemsWithReferenceToClosedOutProducts,
  selectBasketManualDiscountMaxPercentageForEligibleItems,
  selectCanApplyManualDiscount,
  selectDiscountPercentageByBasketLineId,
  selectFutureBasketItems,
  selectManualDiscountValidatonRuleAllowValueBelowCDiscount,
  selectMaterialIdForBasketItemIdsEligibleForManualDiscount,
  selectOpenedDtoBasketETag,
  selectOpenedDtoBasketFromEntity,
  selectOrderTypeForBasketItemIdsEligibleForManualDiscount,
  selectUserIsAllowedToSeeManualDiscountsForNewishBaskets,
} from '../selectors';
import { DecimalPipe } from '@angular/common';

type MaxDiscountPercentageForProductResponseType =
  | {
      [materialId: string]: { value: number };
    }
  | undefined;

type DiscountByMaterialIdByOrderType = Partial<{
  [k in OrderType]: { [materialId: string]: number };
}>;

@Injectable()
export class BasketManualDiscountsEffects {
  constructor(
    private readonly actions$: Actions,
    private readonly configurationsService: ConfigurationsService,
    private readonly store: Store,
    private readonly basketService: CBasketService,
    private decimalPipe: DecimalPipe,
  ) {}

  public loadBasketManualDiscountsWhenBasketWasLoaded$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadDtoBasketSuccess),
      concatLatestFrom(() => [
        this.store.select(
          selectUserIsAllowedToSeeManualDiscountsForNewishBaskets,
        ),
      ]),
      filter(
        ([{ basket }, userIsAllowedToSeeManualDiscountsForNewishBaskets]) => {
          const status = basket.status.toUpperCase() as BasketStatus;
          const isNewish = isBasketStatusNewish(status);
          return !isNewish || userIsAllowedToSeeManualDiscountsForNewishBaskets;
        },
      ),
      map(([{ basket }]) => {
        return this.loadBasketManualDiscountsFromBasket(basket);
      }),
    ),
  );

  public propagateApplyManualDiscountsInvalidatedReasons$ = createEffect(() =>
    this.actions$.pipe(
      ofType(applyBasketManualDiscountsInvalidated),
      mergeMap(({ basketId, reason }) => {
        return [reason, resetBasketManualDiscountsInputs({ basketId })];
      }),
    ),
  );

  public validateApplyManualDiscounts$ = createEffect(() =>
    this.actions$.pipe(
      ofType(applyBasketManualDiscounts),
      concatLatestFrom(() => [
        this.store.select(
          selectBasketManualDiscountMaxPercentageForEligibleItems,
        ),
        this.store.select(selectDiscountPercentageByBasketLineId),
        this.store.select(
          selectManualDiscountValidatonRuleAllowValueBelowCDiscount,
        ),
        this.store
          .select(selectBasketItemsWithReferenceToClosedOutProducts)
          .pipe(map((basketItems) => basketItems.map((bi) => bi.basketItemId))),
        this.store
          .select(selectFutureBasketItems)
          .pipe(map((basketItems) => basketItems.map((bi) => bi.basketItemId))),
      ]),
      mergeMap(
        ([
          applyManualDiscountsAction,
          maxPercentageForEligibleItems,
          discountPercentageByBasketLineId,
          allowValueBelowCDiscount,
          basketItemIdsWithReferenceToClosedOutProducts,
          futureBasketItemIds,
        ]) => {
          let validBasketItemIds: string[] = Object.keys(
            applyManualDiscountsAction.values,
          );

          const actions: (
            | ReturnType<typeof applyBasketManualDiscountsInvalidated>
            | ReturnType<typeof applyBasketManualDiscountsValidated>
          )[] = [];

          const maxDiscountPercentageValidation =
            this.validateMaxDiscountPercentage(
              applyManualDiscountsAction,
              maxPercentageForEligibleItems,
            );

          if (maxDiscountPercentageValidation) {
            actions.push(maxDiscountPercentageValidation);

            const reason = maxDiscountPercentageValidation.reason as ReturnType<
              typeof maxBasketManualDiscountValueExceeded
            >;
            const basketItemIdsAboveTheirMaxAllowed = Object.keys(
              reason.values,
            );
            validBasketItemIds = validBasketItemIds.filter(
              (biid) => !basketItemIdsAboveTheirMaxAllowed.includes(biid),
            );
          }

          if (validBasketItemIds.length === 0) {
            return actions;
          }

          const validateValueToBeAboveCDiscount =
            this.validateValueToBeAboveOrEqualToCDiscount(
              applyManualDiscountsAction,
              discountPercentageByBasketLineId,
              allowValueBelowCDiscount,
              basketItemIdsWithReferenceToClosedOutProducts,
              futureBasketItemIds,
            );

          if (validateValueToBeAboveCDiscount !== undefined) {
            actions.push(validateValueToBeAboveCDiscount.invalidated);

            validBasketItemIds = Object.keys(
              applyManualDiscountsAction.values,
            ).filter(
              (bid) =>
                !validateValueToBeAboveCDiscount.basketItemIdsBelowTheirCDiscount.includes(
                  bid,
                ),
            );
          }

          if (validBasketItemIds.length > 0) {
            const validValues: { [basketItemId: string]: number } = {};
            validBasketItemIds.forEach((bid) => {
              validValues[bid] = applyManualDiscountsAction.values[bid];
            });

            actions.push(
              applyBasketManualDiscountsValidated({
                basketId: applyManualDiscountsAction.basketId,
                applyBasketManualDiscountsAction: {
                  ...applyManualDiscountsAction,
                  values: validValues,
                },
              }),
            );
          }

          return actions;
        },
      ),
    ),
  );

  public removeAllWhenClearingManualDiscountsForBasket$ = createEffect(() =>
    this.actions$.pipe(
      ofType(clearBasketManualDiscounts),
      concatLatestFrom(() => this.store.select(selectAllBasketManualDiscounts)),
      map(([action, allManualDiscounts]) => {
        const basketId = action.basketId;
        const basketItemIdsFromAction = action.basketItemIds;

        const manualDiscountBasketItemIdsForBasket = allManualDiscounts
          .filter((md) => md.basketId === basketId)
          .map((md) => md.basketItemId);

        const basketItemIds =
          basketItemIdsFromAction !== undefined &&
          basketItemIdsFromAction.length > 0
            ? basketItemIdsFromAction
            : manualDiscountBasketItemIdsForBasket;

        return { basketId, basketItemIds };
      }),
      filter(({ basketItemIds }) => {
        return basketItemIds.length > 0;
      }),
      map(({ basketId, basketItemIds }) => {
        return removeBasketManualDiscounts({
          basketId,
          basketItemIds,
        });
      }),
    ),
  );

  public showMessageWhenManualDiscountValueBelowCDiscount$ = createEffect(() =>
    this.actions$.pipe(
      ofType(manualDiscountValueBelowCDiscount),
      map(({ invalidated }) => {
        const manualDiscount = this.roundValue(
          invalidated[0].manualDiscount * 100,
        );

        const cDiscount = this.roundValueToFloatOrInteger(
          invalidated[0].cdiscount * 100,
        );

        const message =
          invalidated.length === 1
            ? $localize`:Basket|Manual discount may not be below cdiscount@@basketDiscountsValueMayNotBeEqualOrBelowCDiscount:A manual discount of ${manualDiscount}% was not applied because it is below the calculated discount of ${cDiscount}%.`
            : $localize`:Basket|Manual discount may not be below cdiscount@@basketDiscountsValueMayNotBeEqualOrBelowCDiscount:Some manual discounts were not applied because they are below the calculated discount.`;

        return showSnackBarMessage({
          message,
          messageType: MessageType.WARNING,
        });
      }),
    ),
  );

  public showMessageWhenManualDiscountValueExceedsLimit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(maxBasketManualDiscountValueExceeded),
      map(({ values }) => {
        const allValues = Object.values(values);
        const allUniqueValues: { enteredValue: number; maxValue: number }[] =
          uniqueBy(allValues, 'enteredValue', 'maxValue').map((item) => {
            return {
              enteredValue: item.enteredValue * 100,
              maxValue: item.maxValue * 100,
            };
          });

        let message: string;
        if (allUniqueValues.length === 1) {
          let { maxValue, enteredValue } = allUniqueValues[0];
          enteredValue = this.roundValue(enteredValue);
          maxValue = this.roundValue(maxValue);
          message = $localize`:Basket|Manual discount may not be higher than max discount@@basketDiscountsValueMayNotBeHigherThanMaxDiscount:A manual discount of ${enteredValue}% was not applied because it is higher than the maximum discount of ${maxValue}% allowed.`;
        } else {
          const cap = 4;

          const joinedStr = allUniqueValues
            .slice(0, cap)
            .map(
              ({ enteredValue, maxValue }) =>
                `${this.roundValue(enteredValue)} > ${this.roundValue(
                  maxValue,
                )}`,
            )
            .join(' - ');

          const nrOfItemsCapped = allUniqueValues.length - cap;
          if (nrOfItemsCapped > 0) {
            message = $localize`:Basket|Manual discounts may not be higher than max discount capped version@@basketDiscountsValueMayNotBeHigherThanMaxDiscountCappedVersion:Some manual discounts were were not applied because they are higher than what is allowed by their maximum discount: ${joinedStr} (and ${nrOfItemsCapped} more).`;
          } else {
            message = $localize`:Basket|Manual discounts may not be higher than max discount@@basketDiscountsValueMayNotBeHigherThanMaxDiscount:Some manual discounts were were not applied because they are higher than what is allowed by their maximum discount: ${joinedStr}.`;
          }
        }

        return showSnackBarMessage({
          message,
          messageType: MessageType.WARNING,
        });
      }),
    ),
  );

  public reloadBasketSilentlyAfterManualDiscountsUpdatedSuccesfully$ =
    createEffect(() =>
      this.actions$.pipe(
        ofType(manualDiscountsUpdatedSuccess, manualDiscountsDeleteSuccess),
        concatLatestFrom(() => [this.store.select(selectOpenedDtoBasketETag)]),
        switchMap(([{ basketId }, etag]) => {
          return this.basketService.pollOnceDtoBasket(basketId, etag).pipe(
            filter((basketDto) => !!basketDto),
            map((basketDto) => loadDtoBasketSuccess({ basket: basketDto! })),
            catchError((err) => {
              console.warn(err);
              return EMPTY;
            }),
          );
        }),
      ),
    );

  public reloadManualDiscountsFromCurrentBasketDataWhenUpdateFailed$ =
    createEffect(() =>
      this.actions$.pipe(
        ofType(manualDiscountsUpdatedFailed),
        concatLatestFrom(() => [
          this.store.select(selectOpenedDtoBasketFromEntity),
        ]),
        map(([_, basketFromEntity]) =>
          this.loadBasketManualDiscountsFromBasket(
            basketFromEntity as ExtendedDtoBasket,
          ),
        ),
      ),
    );

  public showMessageWhenReverseCalculationForDiscountPercentageFromNetTotalValueFailed$ =
    createEffect(() =>
      this.actions$.pipe(
        ofType(reverseCalculationForDiscountPercentageFromNetTotalValueFailed),
        map(({ message }) => {
          const messageToShow = $localize`:Basket|Manual discount reverse calculation for discount percentage from net total value failed@@basketDiscountsManualReverseCalculationForDiscountPercentageFromNetTotalValueFailed:The calculation failed and therefore no discounts were applied. Technical error: ${message}`;
          return showSnackBarMessage({
            message: messageToShow,
            messageType: MessageType.DANGER,
          });
        }),
      ),
    );

  private configWasLoadedOnce = false;

  public triggerToLoadMaxDiscountPercentage$ = createEffect(() =>
    merge(
      this.actions$.pipe(
        ofType(initConfigSuccess),
        filter((action) => action.namespaces.includes(CBASKET_NAMESPACE)),
        tap(() => {
          // To prevent race conditions were config values are overwritten, wait for the config
          // of cbasket namespace to have been initialized
          // Before overwriting
          if (!this.configWasLoadedOnce) {
            this.configWasLoadedOnce = true;
          }
        }),
      ),
      this.actions$.pipe(
        ofType(updateRules),
        filter(() => this.configWasLoadedOnce),
      ),
    ).pipe(
      map(() => {
        return loadMaxDiscountPercentage();
      }),
    ),
  );

  public loadMaxDiscountPercentage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadMaxDiscountPercentage),
      switchMap(() => {
        const maxDiscountForNow$ = this.loadMaxDiscountPercentageFor$(
          OrderType.Now,
        );
        const maxDiscountForFuture$ = this.loadMaxDiscountPercentageFor$(
          OrderType.Future,
        );

        return forkJoin([maxDiscountForNow$, maxDiscountForFuture$]).pipe(
          map(([maxDiscountForNow, maxDiscountForFuture]) => {
            const maxDiscountPercentageValue: MaxDiscountPercentageValue = {
              FUTURE: maxDiscountForFuture,
              NOW: maxDiscountForNow,
            };

            return loadMaxDiscountPercentageSuccess({
              maxDiscountPercentageValue,
            });
          }),
        );
      }),
    ),
  );

  public setMaxDiscountPercentageConfigValueWhenLoadedSuccesfully$ =
    createEffect(() =>
      this.actions$.pipe(
        ofType(loadMaxDiscountPercentageSuccess),
        map(({ maxDiscountPercentageValue }) => {
          return upsertToConfig({
            namespace: CBASKET_NAMESPACE,
            featureName: CBasketConfigurationKeys.MaxDiscountPercentage,
            value: JSON.stringify(maxDiscountPercentageValue),
          });
        }),
      ),
    );

  public triggerToLoadMaxDiscountPercentageForBasketItems$ = createEffect(() =>
    combineLatest([
      this.store.select(
        selectMaterialIdForBasketItemIdsEligibleForManualDiscount,
      ),
      this.store.select(selectCanApplyManualDiscount),
    ]).pipe(
      filter(([, canApplyManualDiscount]) => {
        return canApplyManualDiscount;
      }),
      concatLatestFrom(() => [
        this.store.select(
          selectOrderTypeForBasketItemIdsEligibleForManualDiscount,
        ),
      ]),
      map(([[materialIdForBasketItemIds], orderTypeForBasketItemIds]) => {
        return { materialIdForBasketItemIds, orderTypeForBasketItemIds };
      }),
      filter(({ materialIdForBasketItemIds }) => {
        return Object.keys(materialIdForBasketItemIds).length > 0;
      }),
      distinctUntilChangedBy(
        ({ materialIdForBasketItemIds, orderTypeForBasketItemIds }) => {
          const basketItemIds = Object.keys(materialIdForBasketItemIds).sort();

          return {
            basketItemIds,
            materialIds: basketItemIds.map(
              (basketItemId) => materialIdForBasketItemIds[basketItemId],
            ),
            orderTypes: basketItemIds.map(
              (orderType) => orderTypeForBasketItemIds[orderType],
            ),
          };
        },
      ),
      map(({ materialIdForBasketItemIds, orderTypeForBasketItemIds }) => {
        const basketItemIds = Object.keys(materialIdForBasketItemIds);

        const materialIdsByOrderType: Partial<{
          [orderType in OrderType]: string[];
        }> = {};

        basketItemIds.forEach((basketItemId) => {
          const orderType = orderTypeForBasketItemIds[basketItemId];
          const materialIds = materialIdsByOrderType[orderType] ?? [];
          const materialId = materialIdForBasketItemIds[basketItemId];
          materialIds.push(materialId);
          materialIdsByOrderType[orderType] = materialIds;
        });

        return loadMaxDiscountPercentageForMaterials({
          materialIdsByOrderType,
        });
      }),
    ),
  );

  public loadMaxDiscountPercentageForMaterials$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadMaxDiscountPercentageForMaterials),
      switchMap((action) => {
        const responses$: ReturnType<
          BasketManualDiscountsEffects['getMaxDiscountPercentageConfigValuesFor']
        >[] = [];

        Object.entries(action.materialIdsByOrderType).forEach(
          ([orderType, materialIds]) => {
            const response$ = this.getMaxDiscountPercentageConfigValuesFor(
              orderType as OrderType,
              materialIds,
            );

            responses$.push(response$);
          },
        );

        return forkJoin(responses$).pipe(
          map((orderTypesWithValues) => {
            const actionPayload =
              this.combineOrderTypesWithDiscountValues(orderTypesWithValues);

            return loadMaxDiscountPercentageForMaterialsSuccess({
              discountPercentages: actionPayload,
            });
          }),
        );
      }),
    ),
  );

  private getMaxDiscountPercentageConfigValuesFor(
    orderType: OrderType,
    materialIds: string[],
  ): Observable<{
    orderType: OrderType;
    values: MaxDiscountPercentageForProductResponseType;
  }> {
    return this.configurationsService
      .fetchConfigValue({
        namespace: '', // Leaving this empty, CBASKET_NAMESPACE could have been used, but this causes all rules in that namespace to be returned.
        featureName: CBasketConfigurationKeys.MaxDiscountPercentage,
        context: {
          productIds: materialIds,
          orderType,
        },
      })
      .pipe(
        map((response: MaxDiscountPercentageForProductResponseType) => {
          return {
            orderType,
            values: response,
          };
        }),
      );
  }

  private combineOrderTypesWithDiscountValues(
    orderTypesWithValues: {
      orderType: OrderType;
      values: MaxDiscountPercentageForProductResponseType;
    }[],
  ): DiscountByMaterialIdByOrderType {
    const actionPayload: DiscountByMaterialIdByOrderType = {};

    orderTypesWithValues.forEach((orderTypeWithValues) => {
      const orderType = orderTypeWithValues.orderType;

      const actionPayloadForThisOrderType: {
        [materialId: string]: number;
      } = {};

      if (orderTypeWithValues.values !== undefined) {
        Object.entries(orderTypeWithValues.values).forEach(
          ([materialId, { value }]) => {
            actionPayloadForThisOrderType[materialId] = value;
          },
        );
      }

      actionPayload[orderType] = actionPayloadForThisOrderType;
    });

    return actionPayload;
  }

  private validateMaxDiscountPercentage(
    { basketId, values }: ReturnType<typeof applyBasketManualDiscounts>,
    maxDiscountPercentagePerBasketLineId: {
      [basketLineId: string]: number;
    },
  ) {
    const valuesAboveMaxDiscountPercentage: {
      [basketItemId: string]: {
        enteredValue: number;
        maxValue: number;
      };
    } = {};

    Object.entries(values).forEach(([basketItemId, value]) => {
      const maxForBasketItem =
        maxDiscountPercentagePerBasketLineId[basketItemId];

      if (value > maxForBasketItem) {
        valuesAboveMaxDiscountPercentage[basketItemId] = {
          enteredValue: value,
          maxValue: maxForBasketItem,
        };
      }
    });

    if (Object.keys(valuesAboveMaxDiscountPercentage).length === 0) {
      return undefined;
    }

    const reason = maxBasketManualDiscountValueExceeded({
      values: valuesAboveMaxDiscountPercentage,
    });

    return applyBasketManualDiscountsInvalidated({
      basketId,
      reason,
    });
  }

  private validateValueToBeAboveOrEqualToCDiscount(
    { basketId, values }: ReturnType<typeof applyBasketManualDiscounts>,
    discountPercentageByBasketLineId: {
      [basketLineId: string]: number | undefined;
    },
    allowValueBelowCDiscount: ManualDiscountsValidationCDiscountOrderTypes,
    basketItemIdsWithReferenceToClosedOutProducts: string[],
    futureBasketItemIds: string[],
  ):
    | {
        invalidated: ReturnType<typeof applyBasketManualDiscountsInvalidated>;
        basketItemIdsBelowTheirCDiscount: string[];
      }
    | undefined {
    if (values === null) {
      return;
    }

    const basketItemIdsBelowTheirCDiscount: string[] = [];

    // Prefer pre-order over closed out if both. A pre order closed out item has no meaning to the business.
    basketItemIdsWithReferenceToClosedOutProducts =
      basketItemIdsWithReferenceToClosedOutProducts.filter(
        (id) => !futureBasketItemIds.includes(id),
      );

    // Opt-out stategy: so all are validated by default unless allowed specifically.
    const basketItemIds = Object.keys(values);
    let basketItemIdsToValidate = [...basketItemIds];
    if (allowValueBelowCDiscount.closedOut) {
      basketItemIdsToValidate = basketItemIdsToValidate.filter(
        (id) => !basketItemIdsWithReferenceToClosedOutProducts.includes(id),
      );
    }
    if (allowValueBelowCDiscount.preOrder) {
      basketItemIdsToValidate = basketItemIdsToValidate.filter(
        (id) => !futureBasketItemIds.includes(id),
      );
    }

    for (const [basketLineId, discount] of Object.entries(
      discountPercentageByBasketLineId,
    )) {
      if (
        basketItemIdsToValidate.includes(basketLineId) &&
        basketItemIds.includes(basketLineId) &&
        !!discount &&
        Math.round(discount * 10000) / 10000 > values[basketLineId]
      ) {
        basketItemIdsBelowTheirCDiscount.push(basketLineId);
      }
    }

    if (basketItemIdsBelowTheirCDiscount.length === 0) {
      return;
    }

    const invalidated = basketItemIdsBelowTheirCDiscount.map((bid) => {
      return {
        basketItemId: bid,
        cdiscount: discountPercentageByBasketLineId[bid] as number,
        manualDiscount: values[bid],
      };
    });

    const reason = manualDiscountValueBelowCDiscount({
      basketId,
      invalidated,
    });

    return {
      invalidated: applyBasketManualDiscountsInvalidated({
        basketId,
        reason,
      }),
      basketItemIdsBelowTheirCDiscount,
    };
  }

  private loadBasketManualDiscountsFromBasket(basket: ExtendedDtoBasket) {
    const manualDiscounts: ManualDiscountEntity[] = [];
    basket.items.forEach((item) => {
      const discount = item.prices?.discount;
      if (discount !== undefined) {
        const manualDiscount: ManualDiscountEntity = {
          basketId: basket.basketId,
          basketItemId: item.itemId,
          currentValue: discount,
        };

        manualDiscounts.push(manualDiscount);
      }
    });

    return loadBasketManualDiscounts({
      basketId: basket.basketId,
      entities: manualDiscounts,
    });
  }

  private loadMaxDiscountPercentageFor$(
    orderType: OrderType,
  ): Observable<number> {
    const context: IRuleContext = {
      orderType,
    };

    return this.configurationsService
      .fetchConfigValue({
        namespace: '', // Instead of CBASKET_NAMESPACE leaving it empty or else it returns all rules in that namespace,
        featureName: CBasketConfigurationKeys.MaxDiscountPercentage,
        context,
      })
      .pipe(
        take(1),
        map((maxDiscountPercentageConfigValue) => {
          if (isNumber(maxDiscountPercentageConfigValue)) {
            return maxDiscountPercentageConfigValue;
          } else if (isString(maxDiscountPercentageConfigValue)) {
            const parsedValue = parseFloat(maxDiscountPercentageConfigValue);
            if (isNumber(parsedValue) && !isNaN(parsedValue)) {
              return parsedValue;
            }
          }
          return 0;
        }),
      );
  }

  private roundValue(value: number, nrOfDecimals: number = 2): number {
    const multiplier = Math.pow(10, 2 + nrOfDecimals);
    const newValue = Math.round(value * multiplier) / multiplier;

    return newValue;
  }

  private roundValueToFloatOrInteger(value: number) {
    return this.decimalPipe.transform(value, '1.0-2');
  }
}
